import { Calendar, Camera, Check, Cloud, FileIcon, FileText, Image, MessageSquare, Plus, Tag, Upload, Users, Video, Wrench, X } from "lucide-react";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import CommentCard from "@/features/comunicacao/components/CommentCard";
import DescricaoEditor from "@/features/comunicacao/components/DescricaoEditor";
import DiarioComentariosPanel from "@/features/diario/components/DiarioComentariosPanel";
import { TagCatalogo } from "@/features/midia/services/fotoTagsService";
import { supabase } from "@/features/sistema/services/supabaseClient";

interface MaoItem {
  nome: string;
  quantidade: number;
}
interface EquipItem {
  nome: string;
  quantidade: number;
}
interface AtividadeItem {
  descricao: string;
  porcentagem: number;
  status: string;
}
interface AnexoItem {
  id: string;
  url: string;
  nome: string;
  tipo: "foto" | "video" | "anexo";
  criado_em?: string;
}

interface DiarioFormSectionsProps {
  form: {
    numero: number;
    data: string;
    status: string;
    clima_manha: string | null;
    clima_tarde: string | null;
    clima_noite: string | null;
    mao_obra: MaoItem[];
    equipamentos: EquipItem[];
    atividades: AtividadeItem[];
    ocorrencias: string[];
    comentarios: string;
    anexos: AnexoItem[];
    alteracoes_projeto: { texto: string; anexos?: Array<{url: string; nome: string; tipo: string}> } | null;
  };
  updateField: (field: string, value: any) => void;
  updateQtdMao: (idx: number, nova: number) => void;
  removeMao: (idx: number) => void;
  updateQtdEquip: (idx: number, nova: number) => void;
  removeEquip: (idx: number) => void;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>, tipo: "foto" | "video" | "anexo") => void;
  removeAnexo: (anexo: AnexoItem) => void;
  mediaItems: AnexoItem[];
  openGallery: (index: number) => void;
  modoEscuro: boolean;
  saving: boolean;
  uploading: boolean;
  setShowMaoModal: (v: boolean) => void;
  setShowEquipModal: (v: boolean) => void;
  /** Quando true, renderiza tudo em modo leitura (sem edição, sem botões) */
  readOnly?: boolean;
  /** Quando true, mesmo em readOnly permite o usuário editar/excluir os próprios comentários */
  enableSelfEditOnReadOnly?: boolean;
  /** Quando false, esconde toda a seção de upload/galeria de mídia */
  showMedia?: boolean;
  /** Callback para long press em uma foto (para adicionar tags) */
  onFotoLongPress?: (anexo: AnexoItem, position: { x: number; y: number }) => void;
  /** Modo de seleção múltipla de fotos */
  modoSelecaoFotos?: boolean;
  fotosSelecionadas?: Set<string>;
  onToggleSelecaoFoto?: (fotoId: string) => void;
  onToggleModoSelecao?: () => void;
  onSelecionarTodasFotos?: () => void;
  onLimparSelecaoFotos?: () => void;
  onAbrirTagsMultiplas?: () => void;
  /** Mapa de tags por foto (para exibir indicador) */
  fotoTagsMap?: Map<string, TagCatalogo[]>;
}

const DiarioFormSections: React.FC<DiarioFormSectionsProps> = (props) => {
  const {
    form, updateField, updateQtdMao, removeMao,
    updateQtdEquip, removeEquip,
    handleFileUpload, removeAnexo, mediaItems, openGallery,
    modoEscuro, saving, uploading,
    setShowMaoModal, setShowEquipModal,
    readOnly = false,
    enableSelfEditOnReadOnly = false,
    showMedia = true,
    onFotoLongPress,
    modoSelecaoFotos = false,
    fotosSelecionadas,
    onToggleSelecaoFoto,
    onToggleModoSelecao,
    onSelecionarTodasFotos,
    onLimparSelecaoFotos,
    onAbrirTagsMultiplas,
    fotoTagsMap
  } = props;
  const disabled = saving || readOnly;

  // Refs para long press
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);

  // Handlers de long press para fotos
  const handleFotoTouchStart = (anexo: AnexoItem, e: React.TouchEvent) => {
    if (!onFotoLongPress) return;

    longPressTriggeredRef.current = false;
    const touch = e.touches[0];

    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      if ('vibrate' in navigator) {
        navigator.vibrate(50);
      }
      onFotoLongPress(anexo, { x: touch.clientX, y: touch.clientY });
    }, 500);
  };

  const handleFotoTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleFotoMouseDown = (anexo: AnexoItem, e: React.MouseEvent) => {
    if (!onFotoLongPress || e.button !== 0) return;

    longPressTriggeredRef.current = false;

    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      onFotoLongPress(anexo, { x: e.clientX, y: e.clientY });
    }, 500);
  };

  const handleFotoMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleFotoClick = (_anexo: AnexoItem, mediaIndex: number) => {
    // Se foi long press, não abre a galeria
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    openGallery(mediaIndex);
  };

  // Estado para anexos de Alterações de Projeto
  const [anexosPendentes, setAnexosPendentes] = useState<File[]>([]);

  // Suporta rotas /obras/:id/... (interno) e /cliente/obras/:obraId/... (cliente)
  const params = useParams<{ id?: string; obraId?: string; relatorioId?: string }>();
  const obraIdFromRoute = params.obraId ?? params.id ?? "";
  const relatorioIdFromRoute = params.relatorioId ?? "";

  return (
    <>
      {/* Detalhes do Relatório + Condição Climática (Layout Responsivo) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        {/* Detalhes do Relatório */}
        <section className={`rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
          <h2 className={`text-base lg:text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
            <FileText className="w-4 h-4 lg:w-5 lg:h-5" />
            Detalhes do Diário
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className={`block text-xs lg:text-sm mb-1 transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Número <span className={modoEscuro ? "text-red-400" : "text-critical"}>*</span></label>
              <input
                type="number"
                value={form.numero}
                onChange={(e) => !readOnly && updateField('numero', Number(e.target.value))}
                className={`w-full rounded-md border px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm font-ageo-regular focus:outline-none focus:ring-2 transition-colors ${modoEscuro ? "border-gray-600 bg-gray-700 text-gray-100 focus:ring-blue-400" : "border-secondary bg-background text-foreground focus:ring-primary"}`}
                min="1"
                required
                disabled={disabled}
              />
            </div>
            <div>
              <label className={`block text-xs lg:text-sm mb-1 transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Data <span className={modoEscuro ? "text-red-400" : "text-critical"}>*</span></label>
              <div className="relative">
                <Calendar className={`absolute left-2 lg:left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 lg:w-4 lg:h-4 transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/50"}`} />
                <input
                  type="date"
                  value={form.data}
                  onChange={(e) => !readOnly && updateField('data', e.target.value)}
                  className={`w-full pl-7 lg:pl-9 rounded-md border px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm font-ageo-regular focus:outline-none focus:ring-2 transition-colors ${modoEscuro ? "border-gray-600 bg-gray-700 text-gray-100 focus:ring-blue-400" : "border-secondary bg-background text-foreground focus:ring-primary"}`}
                  required
                  disabled={disabled}
                />
              </div>
            </div>
            <div>
              <label className={`block text-xs lg:text-sm mb-1 transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Status</label>
              <select
                value={form.status}
                onChange={(e) => !readOnly && updateField('status', e.target.value)}
                className={`w-full rounded-md border px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm font-ageo-regular focus:outline-none focus:ring-2 transition-colors ${modoEscuro ? "border-gray-600 bg-gray-700 text-gray-100 focus:ring-blue-400" : "border-secondary bg-background text-foreground focus:ring-primary"}`}
                disabled={disabled}
              >
                <option value="rascunho">Rascunho</option>
                <option value="submetido">Submetido</option>
                <option value="aprovado">Aprovado</option>
              </select>
            </div>
          </div>
        </section>

        {/* Condição Climática */}
        <section className={`rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
          <h2 className={`text-base lg:text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
            <Cloud className="w-4 h-4 lg:w-5 lg:h-5" />
            Condição Climática
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {(['Manhã', 'Tarde', 'Noite'] as const).map((periodo) => {
              const field = periodo === 'Manhã' ? 'clima_manha' : periodo === 'Tarde' ? 'clima_tarde' : 'clima_noite';
              const valor = form[field] as string | null;
              return (
                <div key={periodo} className={`border rounded-lg p-2 transition-colors ${modoEscuro ? "border-gray-600" : "border-secondary"}`}>
                  <h3 className={`font-ageo-bold mb-1 text-xs lg:text-sm transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{periodo}</h3>
                  <div className="space-y-1">
                    {['Claro', 'Nublado', 'Chuvoso'].map((condicao) => (
                      <label key={condicao} className="flex items-center">
                        <input
                          type="radio"
                          name={`clima-${field}`}
                          value={condicao}
                          checked={valor === condicao}
                          onChange={(e) => !readOnly && updateField(field, e.target.value)}
                          className={`h-3 w-3 lg:h-4 lg:w-4 focus:ring-2 ${modoEscuro ? "text-green-600 focus:ring-blue-400" : "text-confirmation focus:ring-primary"}`}
                          disabled={disabled}
                        />
                        <span className={`ml-1 lg:ml-2 text-xs font-ageo-regular transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{condicao}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

       {/* Comentários (esq)  |  Mão de Obra + Equipamentos + Alterações (dir) */}
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
         {/* ESQUERDA: Comentários dinâmicos */}
         <DiarioComentariosPanel
           obraId={obraIdFromRoute}
           relatorioId={relatorioIdFromRoute}
           dark={modoEscuro}
           allowCreate={true}
           allowEdit={enableSelfEditOnReadOnly ? true : !readOnly}
           allowDelete={enableSelfEditOnReadOnly ? true : !readOnly}
           tone={readOnly ? 'white' : 'background'}
         />

         {/* DIREITA: Mão de Obra → Equipamentos → Alterações */}
         <div className="flex flex-col gap-2 lg:col-start-2">
           {/* Mão de Obra (mantém conteúdo original) */}
           <section className={`rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
             <h2 className={`text-base lg:text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
               <Users className="w-4 h-4 lg:w-5 lg:h-5" />
               Mão de Obra ({form.mao_obra.length})
             </h2>
             {!readOnly && (
             <button
               type="button"
               onClick={() => setShowMaoModal(true)}
               className={`mb-2 px-2 lg:px-3 py-1 lg:py-2 rounded-md text-xs lg:text-sm font-ageo-regular transition-colors flex items-center gap-1 lg:gap-2 ${modoEscuro ? "bg-green-600 text-white hover:bg-green-700" : "bg-confirmation text-white hover:bg-confirmation/90"}`}
               disabled={saving}
             >
               <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
               Adicionar
             </button>
             )}
             {form.mao_obra.length > 0 ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 lg:gap-2">
                 {form.mao_obra.map((m: MaoItem, idx: number) => (
                   <div key={idx} className={`flex items-center justify-between p-1 lg:p-2 rounded-md transition-colors ${modoEscuro ? "bg-gray-700/50" : "bg-secondary/10"}`}>
                     <div className={`font-ageo-regular text-xs lg:text-sm transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{m.nome}</div>
                    <div className="flex items-center space-x-1 lg:space-x-2">
                      {readOnly ? (
                        <span className={`font-ageo-bold text-xs lg:text-sm ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{m.quantidade}</span>
                      ) : (
                        <>
                          <button type="button" onClick={() => updateQtdMao(idx, m.quantidade - 1)} className={`px-1 lg:px-2 transition-colors ${modoEscuro ? "text-gray-100 hover:text-green-400" : "text-foreground hover:text-confirmation"}`} disabled={saving}>−</button>
                          <input
                            type="number"
                            value={m.quantidade}
                            onChange={(e) => updateQtdMao(idx, Math.max(0, Number(e.target.value) || 0))}
                            className={`w-12 lg:w-14 text-center font-ageo-bold text-xs lg:text-sm rounded border px-1 py-0.5 transition-colors ${modoEscuro ? "bg-gray-700 border-gray-600 text-gray-100" : "bg-background border-secondary text-foreground"}`}
                            min="0"
                            disabled={saving}
                          />
                          <button type="button" onClick={() => updateQtdMao(idx, m.quantidade + 1)} className={`px-1 lg:px-2 transition-colors ${modoEscuro ? "text-gray-100 hover:text-green-400" : "text-foreground hover:text-confirmation"}`} disabled={saving}>+</button>
                          <button type="button" onClick={() => removeMao(idx)} className={`ml-1 lg:ml-2 transition-colors ${modoEscuro ? "text-red-400 hover:text-red-300" : "text-critical hover:text-critical/80"}`} disabled={saving}><X className="w-3 h-3 lg:w-4 lg:h-4" /></button>
                        </>
                      )}
                    </div>
                   </div>
                 ))}
               </div>
             ) : (
               <p className={`text-xs lg:text-sm font-ageo-regular transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Nenhuma mão de obra adicionada</p>
             )}
           </section>

           {/* Equipamentos (mantém conteúdo original) */}
           <section className={`rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
             <h2 className={`text-base lg:text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
               <Wrench className="w-4 h-4 lg:w-5 lg:h-5" />
               Equipamentos ({form.equipamentos.length})
             </h2>
             {!readOnly && (
             <button
               type="button"
               onClick={() => setShowEquipModal(true)}
               className={`mb-2 px-2 lg:px-3 py-1 lg:py-2 rounded-md text-xs lg:text-sm font-ageo-regular transition-colors flex items-center gap-1 lg:gap-2 ${modoEscuro ? "bg-green-600 text-white hover:bg-green-700" : "bg-confirmation text-white hover:bg-confirmation/90"}`}
               disabled={saving}
             >
               <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
               Adicionar
             </button>
             )}
             {form.equipamentos.length > 0 ? (
               <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 lg:gap-2">
                 {form.equipamentos.map((eq: EquipItem, idx: number) => (
                   <div key={idx} className={`flex items-center justify-between p-1 lg:p-2 rounded-md transition-colors ${modoEscuro ? "bg-gray-700/50" : "bg-secondary/10"}`}>
                     <div className={`font-ageo-regular text-xs lg:text-sm transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{eq.nome}</div>
                    <div className="flex items-center space-x-1 lg:space-x-2">
                      {readOnly ? (
                        <span className={`font-ageo-bold text-xs lg:text-sm transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>{eq.quantidade}</span>
                      ) : (
                        <>
                          <button type="button" onClick={() => updateQtdEquip(idx, eq.quantidade - 1)} className={`px-1 lg:px-2 transition-colors ${modoEscuro ? "text-gray-100 hover:text-green-400" : "text-foreground hover:text-confirmation"}`} disabled={saving}>−</button>
                          <input
                            type="number"
                            value={eq.quantidade}
                            onChange={(e) => updateQtdEquip(idx, Math.max(0, Number(e.target.value) || 0))}
                            className={`w-12 lg:w-14 text-center font-ageo-bold text-xs lg:text-sm rounded border px-1 py-0.5 transition-colors ${modoEscuro ? "bg-gray-700 border-gray-600 text-gray-100" : "bg-background border-secondary text-foreground"}`}
                            min="0"
                            disabled={saving}
                          />
                          <button type="button" onClick={() => updateQtdEquip(idx, eq.quantidade + 1)} className={`px-1 lg:px-2 transition-colors ${modoEscuro ? "text-gray-100 hover:text-green-400" : "text-foreground hover:text-confirmation"}`} disabled={saving}>+</button>
                          <button type="button" onClick={() => removeEquip(idx)} className={`ml-1 lg:ml-2 transition-colors ${modoEscuro ? "text-red-400 hover:text-red-300" : "text-critical hover:text-critical/80"}`} disabled={saving}><X className="w-3 h-3 lg:w-4 lg:h-4" /></button>
                        </>
                      )}
                    </div>
                   </div>
                 ))}
               </div>
             ) : (
               <p className={`text-xs lg:text-sm font-ageo-regular transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Nenhum equipamento adicionado</p>
             )}
           </section>

          {/* Alterações de Projeto com anexos */}
          <section className={`rounded-lg shadow-md border p-2 lg:p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
            <h2 className={`text-base lg:text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
              <MessageSquare className="w-4 h-4 lg:w-5 lg:h-5" />
              Alterações de Projeto
            </h2>
            
            {!readOnly ? (
              <DescricaoEditor
                value={form.alteracoes_projeto?.texto ?? ""}
                onChange={(texto) => updateField('alteracoes_projeto', { 
                  ...form.alteracoes_projeto,
                  texto 
                })}
                placeholder="Descreva as alterações de projeto… Use @ para mencionar usuários"
                disabled={disabled}
                showToolbar={true}
                minRows={3}
                maxRows={10}
                onPasteFiles={async (files) => {
                  if (disabled) return;
                  // Upload dos arquivos
                  const anexosExistentes = form.alteracoes_projeto?.anexos || [];
                  const novosAnexos = [...anexosExistentes];
                  
                  for (const file of files) {
                    try {
                      const fileName = `alteracoes-projeto/${Date.now()}_${file.name}`;
                      const { error: uploadError } = await supabase.storage
                        .from('anexos')
                        .upload(fileName, file, { cacheControl: '3600', upsert: false });
                      
                      if (uploadError) {
                        console.error('Erro no upload:', uploadError);
                        continue;
                      }
                      
                      const { data: urlData } = supabase.storage
                        .from('anexos')
                        .getPublicUrl(fileName);
                      
                      if (urlData.publicUrl) {
                        novosAnexos.push({
                          url: urlData.publicUrl,
                          nome: file.name,
                          tipo: file.type?.startsWith('image/') ? 'imagem' : 
                                file.type?.startsWith('video/') ? 'video' : 'arquivo'
                        });
                      }
                    } catch (e) {
                      console.error('Erro ao processar anexo:', e);
                    }
                  }
                  
                  updateField('alteracoes_projeto', {
                    ...form.alteracoes_projeto,
                    anexos: novosAnexos
                  });
                  setAnexosPendentes([]);
                }}
                pendingFiles={anexosPendentes}
                showPendingPreview={true}
                onRemovePendingFile={(file) => {
                  setAnexosPendentes(cur => cur.filter(f => 
                    !(f.name === file.name && f.size === file.size)
                  ));
                }}
              />
            ) : (
              <div className={`rounded-md border px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm font-ageo-regular ${modoEscuro ? "border-gray-600 bg-gray-700 text-gray-100" : "border-secondary bg-background text-foreground"}`}>
                {form.alteracoes_projeto?.texto || <span className="opacity-50">Nenhuma alteração registrada</span>}
              </div>
            )}
            
            {/* Exibir anexos existentes */}
            {(form.alteracoes_projeto?.anexos?.length ?? 0) > 0 && (
              <div className="mt-3">
                <div className={`text-xs font-semibold mb-2 ${modoEscuro ? "text-gray-300" : "text-foreground/70"}`}>
                  Anexos ({form.alteracoes_projeto?.anexos?.length})
                </div>
                <CommentCard
                  c={{
                    id: 'alteracoes-preview',
                    createdAt: new Date(),
                    authorName: '',
                    authorId: '',
                    content: '',
                    type: 'arquivo',
                    anexos: form.alteracoes_projeto?.anexos || [],
                    likedByMe: false,
                    likesCount: 0
                  }}
                  canEdit={false}
                  canDelete={false}
                  onSaveEdit={() => {}}
                  onDelete={() => {}}
                />
              </div>
            )}
          </section>
         </div>
       </div>

            {/* (limpeza): removido bloco comentado da Galeria de Mídia */}

      {/* Upload de Fotos - LARGURA TOTAL - MINIATURAS OTIMIZADAS */}
      {showMedia && !readOnly && (
      <section className={`rounded-lg shadow-md border p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <h2 className={`text-lg font-ageo-bold flex items-center gap-2 transition-colors flex-shrink-0 ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
            <Camera className="w-5 h-5" />
            <span>Fotos ({form.anexos.filter(a => a.tipo === "foto").length})</span>
          </h2>

          {/* Controles de seleção */}
          {onToggleModoSelecao && form.anexos.filter(a => a.tipo === "foto").length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap justify-end">
              {modoSelecaoFotos ? (
                <>
                  <span className={`text-xs ${modoEscuro ? 'text-gray-400' : 'text-gray-500'}`}>
                    {fotosSelecionadas?.size || 0} sel.
                  </span>
                  {onSelecionarTodasFotos && (
                    <button
                      type="button"
                      onClick={onSelecionarTodasFotos}
                      className={`text-xs px-2 py-1 rounded transition-colors ${
                        modoEscuro
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Todas
                    </button>
                  )}
                  {(fotosSelecionadas?.size || 0) > 0 && (
                    <>
                      {onLimparSelecaoFotos && (
                        <button
                          type="button"
                          onClick={onLimparSelecaoFotos}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            modoEscuro
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Limpar
                        </button>
                      )}
                      {onAbrirTagsMultiplas && (
                        <button
                          type="button"
                          onClick={onAbrirTagsMultiplas}
                          className="text-xs px-2 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors flex items-center gap-1"
                        >
                          <Tag className="w-3 h-3" />
                          Tags
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={onToggleModoSelecao}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      modoEscuro
                        ? 'bg-red-900/50 text-red-300 hover:bg-red-900'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    Cancelar
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onToggleModoSelecao}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    modoEscuro
                      ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900'
                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  }`}
                  title="Selecionar múltiplas fotos para adicionar tags"
                >
                  Selecionar
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Botões no mobile (acima das imagens) */}
        {/* Nota: em iOS/Android, "capture" abre a câmera e retorna 1 arquivo; para múltiplas fotos use a Galeria. */}
        <div className="grid grid-cols-2 gap-2 mb-2 md:hidden">
          {/* Botão Câmera */}
          <label className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-green-600/60 cursor-not-allowed" : "bg-confirmation/60 cursor-not-allowed") : (modoEscuro ? "bg-green-600 hover:bg-green-700" : "bg-confirmation hover:bg-confirmation/90")}`}>
            <Camera className="w-5 h-5" />
            <span>{uploading ? "Enviando..." : "Abrir Câmera"}</span>
            <input type="file" accept="image/*" capture="environment"
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
              onChange={(e) => handleFileUpload(e, "foto")}
              className="hidden" disabled={uploading || saving} />
          </label>
          {/* Botão Galeria */}
          <label className={`inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-blue-600/60 cursor-not-allowed" : "bg-primary/60 cursor-not-allowed") : (modoEscuro ? "bg-blue-600 hover:bg-blue-700" : "bg-primary hover:bg-primary/90")}`}>
            <Image className="w-5 h-5" />
            <span>{uploading ? "Enviando..." : "Galeria (Múltiplas)"}</span>
            <input type="file" accept="image/*" multiple
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
              onChange={(e) => handleFileUpload(e, "foto")}
              className="hidden" disabled={uploading || saving} />
          </label>
        </div>

        {/* Grid de fotos - OTIMIZADO */}
        {form.anexos.filter(a => a.tipo === "foto").length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-1 mb-2 md:mb-3">
            {form.anexos.filter((a: AnexoItem) => a.tipo === "foto").map((anexo: AnexoItem) => {
              const mediaIndex = mediaItems.findIndex(m => m.id === anexo.id);
              const isSelecionada = fotosSelecionadas?.has(anexo.id);
              const tags = fotoTagsMap?.get(anexo.id) || [];
              const hasTags = tags.length > 0;

              const handleClick = () => {
                if (modoSelecaoFotos && onToggleSelecaoFoto) {
                  onToggleSelecaoFoto(anexo.id);
                } else {
                  handleFotoClick(anexo, mediaIndex);
                }
              };

              return (
                <div
                  key={anexo.id}
                  className={`relative group select-none ${
                    isSelecionada ? 'ring-2 ring-blue-500 ring-offset-1 rounded' : ''
                  }`}
                >
                  <img
                    src={anexo.url}
                    alt={`Foto ${anexo.nome}`}
                    className={`w-full h-24 object-cover rounded cursor-pointer transition-opacity ${
                      isSelecionada ? 'opacity-80' : 'hover:opacity-80'
                    }`}
                    onClick={handleClick}
                    onTouchStart={!modoSelecaoFotos ? (e) => handleFotoTouchStart(anexo, e) : undefined}
                    onTouchEnd={!modoSelecaoFotos ? handleFotoTouchEnd : undefined}
                    onTouchCancel={!modoSelecaoFotos ? handleFotoTouchEnd : undefined}
                    onMouseDown={!modoSelecaoFotos ? (e) => handleFotoMouseDown(anexo, e) : undefined}
                    onMouseUp={!modoSelecaoFotos ? handleFotoMouseUp : undefined}
                    onMouseLeave={!modoSelecaoFotos ? handleFotoMouseUp : undefined}
                    loading="lazy"
                    draggable={false}
                  />

                  {/* Indicador de tags (bottom) */}
                  {hasTags && (
                    <div className="absolute bottom-0.5 left-0.5 right-0.5 z-10 flex gap-0.5 flex-wrap overflow-hidden max-h-[32px]">
                      {tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag.id}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-ageo-bold leading-none truncate shadow-sm ${
                            modoEscuro
                              ? 'bg-blue-900/90 text-blue-200'
                              : 'bg-blue-600/90 text-white'
                          }`}
                          title={tag.nome}
                        >
                          {tag.nome.slice(0, 3).toUpperCase()}
                        </span>
                      ))}
                      {tags.length > 3 && (
                        <span className={`px-1 py-0.5 rounded text-[10px] font-ageo-bold leading-none shadow-sm ${
                          modoEscuro
                            ? 'bg-blue-900/90 text-blue-200'
                            : 'bg-blue-600/90 text-white'
                        }`}>
                          +{tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Checkbox de seleção */}
                  {modoSelecaoFotos && (
                    <div className="absolute top-1 left-1 z-10">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelecionada
                          ? 'bg-blue-500 border-blue-500'
                          : modoEscuro
                            ? 'bg-gray-800/80 border-gray-500'
                            : 'bg-white/80 border-gray-400'
                      }`}>
                        {isSelecionada && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  )}

                  {/* Botão de excluir (escondido no modo seleção) */}
                  {!modoSelecaoFotos && (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Tem certeza que deseja excluir esta foto?`)) {
                          removeAnexo(anexo);
                        }
                      }}
                      className={`absolute top-1 right-1 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg ${modoEscuro ? "bg-red-600 text-white hover:bg-red-700" : "bg-critical text-white hover:bg-critical/80"}`}
                      disabled={saving}
                      title="Excluir foto"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className={`text-sm font-ageo-regular transition-colors mb-2 md:mb-3 ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Nenhuma foto adicionada</p>
        )}
        
        {/* Botões no desktop */}
        {/* Nota: em iOS/Android, "capture" abre a câmera e retorna 1 arquivo; para múltiplas fotos use a Galeria. */}
        <div className="hidden md:grid grid-cols-2 gap-2">
          {/* Botão Câmera */}
          <label className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-green-600/60 cursor-not-allowed" : "bg-confirmation/60 cursor-not-allowed") : (modoEscuro ? "bg-green-600 hover:bg-green-700" : "bg-confirmation hover:bg-confirmation/90")}`}>
            <Camera className="w-4 h-4" />
            <span>{uploading ? "Enviando..." : "Abrir Câmera"}</span>
            <input type="file" accept="image/*" capture="environment"
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
              onChange={(e) => handleFileUpload(e, "foto")}
              className="hidden" disabled={uploading || saving} />
          </label>
          {/* Botão Galeria */}
          <label className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-blue-600/60 cursor-not-allowed" : "bg-primary/60 cursor-not-allowed") : (modoEscuro ? "bg-blue-600 hover:bg-blue-700" : "bg-primary hover:bg-primary/90")}`}>
            <Image className="w-4 h-4" />
            <span>{uploading ? "Enviando..." : "Galeria (Múltiplas)"}</span>
            <input type="file" accept="image/*" multiple
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
              onChange={(e) => handleFileUpload(e, "foto")}
              className="hidden" disabled={uploading || saving} />
          </label>
        </div>
      </section>
      )}

      {/* Vídeos e Documentos - DIVIDIDOS EM DUAS COLUNAS - MINIATURAS OTIMIZADAS */}
      {showMedia && !readOnly && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Vídeos */}
        <section className={`rounded-lg shadow-md border p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
          <h2 className={`text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
            <Video className="w-5 h-5" />
            Vídeos ({form.anexos.filter(a => a.tipo === "video").length})
          </h2>
          <div className="grid grid-cols-2 gap-2 mb-2">
            {/* Botão Câmera Vídeo */}
            <label className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-xs font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-green-600/60 cursor-not-allowed" : "bg-confirmation/60 cursor-not-allowed") : (modoEscuro ? "bg-green-600 hover:bg-green-700" : "bg-confirmation hover:bg-confirmation/90")}`}>
              <Video className="w-4 h-4" />
              <span className="hidden sm:inline">{uploading ? "Enviando..." : "Gravar"}</span>
              <input type="file" accept="video/*" capture="environment"
                onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
                onChange={(e) => handleFileUpload(e, "video")}
                className="hidden" disabled={uploading || saving} />
            </label>
            {/* Botão Arquivos Vídeo */}
            <label className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-md text-xs font-ageo-regular text-white cursor-pointer transition-colors ${uploading ? (modoEscuro ? "bg-blue-600/60 cursor-not-allowed" : "bg-primary/60 cursor-not-allowed") : (modoEscuro ? "bg-blue-600 hover:bg-blue-700" : "bg-primary hover:bg-primary/90")}`}>
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{uploading ? "Enviando..." : "Arquivo"}</span>
              <input type="file" accept="video/*" multiple
                onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
                onChange={(e) => handleFileUpload(e, "video")}
                className="hidden" disabled={uploading || saving} />
            </label>
          </div>
          {form.anexos.filter(a => a.tipo === "video").length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {form.anexos.filter((a: AnexoItem) => a.tipo === "video").map((anexo: AnexoItem) => {
                const mediaIndex = mediaItems.findIndex(m => m.id === anexo.id);
                return (
                  <div key={anexo.id} className="relative group">
                    <video
                      src={anexo.url}
                      className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openGallery(mediaIndex)}
                    />
                    <button 
                      type="button" 
                      onClick={() => {
                        if (window.confirm(`Tem certeza que deseja excluir este vídeo?`)) {
                          removeAnexo(anexo);
                        }
                      }} 
                      className={`absolute top-1 right-1 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg ${modoEscuro ? "bg-red-600 text-white hover:bg-red-700" : "bg-critical text-white hover:bg-critical/80"}`} 
                      disabled={saving}
                      title="Excluir vídeo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    {/* Ícone de play para indicar que é vídeo */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      <div className="bg-black/60 rounded-full p-2">
                        <Video className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className={`text-sm font-ageo-regular transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Nenhum vídeo adicionado</p>
          )}
        </section>
        
        {/* Documentos */}
        <section className={`rounded-lg shadow-md border p-3 transition-colors ${modoEscuro ? "bg-gray-800 border-gray-700" : "bg-background border-secondary"}`}>
          <h2 className={`text-lg font-ageo-bold mb-2 flex items-center gap-2 transition-colors ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
            <FileIcon className="w-5 h-5" />
            Documentos ({form.anexos.filter(a => a.tipo === "anexo").length})
          </h2>
          <label className={`inline-block mb-2 px-4 py-2 rounded-md text-sm font-ageo-regular text-white cursor-pointer transition-colors flex items-center gap-2 ${uploading ? (modoEscuro ? "bg-green-600/60 cursor-not-allowed" : "bg-confirmation/60 cursor-not-allowed") : (modoEscuro ? "bg-green-600 hover:bg-green-700" : "bg-confirmation hover:bg-confirmation/90")}`}>
            <Upload className="w-4 h-4" />
            <span>{uploading ? "Enviando..." : "Selecionar Arquivos"}</span>
            <input type="file" multiple
              onClick={(e) => { (e.target as HTMLInputElement).value = ""; }}
              onChange={(e) => handleFileUpload(e, "anexo")}
              className="hidden" disabled={uploading || saving} />
          </label>
          {form.anexos.filter(a => a.tipo === "anexo").length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-1">
              {form.anexos.filter((a: AnexoItem) => a.tipo === "anexo").map((anexo: AnexoItem) => (
                <div key={anexo.id} className={`relative group rounded overflow-hidden transition-colors ${modoEscuro ? "bg-gray-700/50" : "bg-secondary/10"}`}>
                  <a 
                    href={anexo.url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="block w-full h-24 p-2 hover:opacity-80 transition-opacity"
                    title={anexo.nome}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <FileIcon className={`w-6 h-6 mb-1 ${modoEscuro ? "text-blue-400" : "text-primary"}`} />
                      <span className={`text-xs font-ageo-regular text-center line-clamp-2 ${modoEscuro ? "text-gray-100" : "text-foreground"}`}>
                        {anexo.nome.length > 12 ? anexo.nome.substring(0, 12) + '...' : anexo.nome}
                      </span>
                    </div>
                  </a>
                  <button 
                    type="button" 
                    onClick={() => {
                      if (window.confirm(`Tem certeza que deseja excluir o documento "${anexo.nome}"?`)) {
                        removeAnexo(anexo);
                      }
                    }} 
                    className={`absolute top-1 right-1 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all shadow-lg ${modoEscuro ? "bg-red-600 text-white hover:bg-red-700" : "bg-critical text-white hover:bg-critical/80"}`} 
                    disabled={saving}
                    title="Excluir documento"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-sm font-ageo-regular transition-colors ${modoEscuro ? "text-gray-400" : "text-foreground/70"}`}>Nenhum documento adicionado</p>
          )}
        </section>
      </div>
      )}
    </>
  );
};

export default DiarioFormSections;
